/**
 * Pure DOM helpers behind the delegated click listener in
 * BaseAGUAppSurfaceComponent. One capture-phase listener per surface host
 * covers every button vocabulary the three apps use (raw <button>, <a>,
 * [mjButton] hosts, role="button") without touching a single template;
 * `data-track` is the opt-in override for elements whose automatic label is
 * poor (icon-only buttons should carry aria-label anyway, ui-best-practices §4).
 */
import type { UserActivityElementKind } from './user-activity-event';

/** Attribute selectors are case-insensitive on HTML elements, so `[mjButton]` matches the directive's host attribute. */
export const CLICK_TARGET_SELECTOR = 'button, a[href], a[role], [mjButton], [role="button"]';
export const CLICK_LABEL_MAX = 80;

/** The clickable ancestor of `target` inside `host`, or null when the click is on plain content, a disabled control, or outside the host. */
export function ResolveClickTarget(target: EventTarget | null, host: HTMLElement): Element | null {
    if (!(target instanceof Element)) return null;
    const match = target.closest(CLICK_TARGET_SELECTOR);
    if (match === null || !host.contains(match)) return null;
    if (match.hasAttribute('disabled') || match.getAttribute('aria-disabled') === 'true') return null;
    return match;
}

function collapse(text: string | null): string {
    return (text ?? '').replace(/\s+/g, ' ').trim();
}

/** data-track → aria-label → visible text → tag.firstClass → tag; always ≤ CLICK_LABEL_MAX. */
export function DeriveClickLabel(el: Element): string {
    const tracked = collapse(el.getAttribute('data-track'));
    const aria = collapse(el.getAttribute('aria-label'));
    const text = collapse(el.textContent);
    const tag = el.tagName.toLowerCase();
    const firstClass = el.classList.item(0);
    const fallback = firstClass ? `${tag}.${firstClass}` : tag;
    const label = tracked || aria || text || fallback;
    return label.length > CLICK_LABEL_MAX ? label.slice(0, CLICK_LABEL_MAX) : label;
}

export function DeriveElementKind(el: Element): UserActivityElementKind {
    if (el.hasAttribute('mjbutton')) return 'mj-button';
    const tag = el.tagName.toLowerCase();
    if (tag === 'button') return 'button';
    if (tag === 'a') return 'link';
    return 'role-button';
}
